import {
  AdminPanelSettings,
  Close,
  DarkMode,
  GppGood,
  LightMode,
  MenuOutlined,
  Notifications,
  NotificationsActive,
  Work,
} from "@mui/icons-material";
import {
  alpha,
  AppBar,
  Badge,
  Box,
  Button,
  Drawer,
  IconButton,
  Toolbar,
  Tooltip,
  useTheme,
} from "@mui/material";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useColorMode } from "src/context/ColorModeContext";
import { AdminPrivateRoutes, CompanyEezyOnlyRoutes, CompanyPrivateRoutes, EezyGuardAdminRoutes, PsychologistPrivateRoutes } from "src/routes/Routes";
import { isEezyoneEnabled, isUserAdministrator, isUserCompany, isUserCompanyAdmin, isUserPsychologist } from "src/utils/auth";
import { getUnreadNotificationsCount } from "src/apis/user";
import GroupIcon from "@mui/icons-material/Group";
import NotificationsDialog from "./NotificationsDialog";
import { SidebarMyProfileSection } from "./SidebarMyProfileSection";
import SidebarSection from "./SidebarSection";
import { useEezyguardAdmin } from "./eezyguard/common";

type HeaderProps = {
  title?: string;
};

const Header: FC<HeaderProps> = ({ title }): JSX.Element => {
  const theme = useTheme();
  const { mode, toggleColorMode } = useColorMode();
  const { isAdmin: isEezyguardAdmin } = useEezyguardAdmin();
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [isOpenNotifications, setIsOpenNotifications] = useState<boolean>(false);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState<number>(0);

  const fullTitle = useMemo(
    () => `Eezycare${Boolean(title) ? ` | ${title}` : ""}`,
    [title]
  );
  const hasUnreadNotifications = useMemo(
    () => unreadNotificationCount > 0,
    [unreadNotificationCount]
  );

  const fetchNotifications = useCallback(async () => {
    try {
      const count = await getUnreadNotificationsCount(1);
      setUnreadNotificationCount(count);
    } catch {
      // silently ignore
    }
  }, []);

  useEffect(() => {
    document.title = fullTitle;
    fetchNotifications();
  }, [fullTitle, fetchNotifications]);

  const hoverBg = alpha(theme.palette.primary.main, 0.08);

  return (
    <Box>
      <NotificationsDialog
        isOpen={isOpenNotifications}
        setIsOpen={setIsOpenNotifications}
        onReadSuccess={() => setUnreadNotificationCount((old) => old - 1)}
      />

      <AppBar
        variant="outlined"
        position="fixed"
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={() => setIsDrawerOpen(!isDrawerOpen)}
              edge="start"
            >
              {isDrawerOpen ? <Close /> : <MenuOutlined />}
            </IconButton>

            <Button
              href="/"
              variant="text"
              disabled={isDrawerOpen}
              sx={{
                p: "6px 10px",
                minWidth: 0,
                "&:hover": { backgroundColor: hoverBg, borderRadius: 2 },
              }}
            >
              <Box
                component="img"
                src="/logo-horizontal.png"
                alt="Eezycare"
                sx={{
                  height: { xs: 28, sm: 32 },
                  display: "block",
                  filter: mode === "dark" ? "brightness(0) invert(1)" : "none",
                }}
              />
            </Button>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Tooltip title={mode === "dark" ? "Modo claro" : "Modo escuro"}>
              <IconButton color="inherit" onClick={toggleColorMode}>
                {mode === "dark" ? <LightMode /> : <DarkMode />}
              </IconButton>
            </Tooltip>

            <Tooltip
              title={
                hasUnreadNotifications
                  ? `Você tem ${unreadNotificationCount} notificações não lidas`
                  : "Notificações"
              }
            >
              <IconButton
                color="inherit"
                onClick={() => setIsOpenNotifications(true)}
              >
                <Badge badgeContent={unreadNotificationCount} color="primary">
                  {hasUnreadNotifications ? <NotificationsActive /> : <Notifications />}
                </Badge>
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="temporary"
        anchor="left"
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
      >
        <Toolbar />
        <Box sx={{ width: 350 }} role="presentation">
          {isUserCompany() && (
            <SidebarSection
              icon={<Work />}
              name="Área da empresa"
              listItens={(isEezyoneEnabled()
                ? CompanyPrivateRoutes
                : CompanyEezyOnlyRoutes
              ).filter(
                (route) =>
                  route.path !== "/eezyguard/empresa" || isUserCompanyAdmin()
              )}
            />
          )}
          {isUserPsychologist() && (
            <SidebarSection
              icon={<GroupIcon />}
              name="Área do psicólogo"
              listItens={PsychologistPrivateRoutes}
            />
          )}
          {isUserAdministrator() && (
            <SidebarSection
              icon={<AdminPanelSettings />}
              name="Área do administrador"
              listItens={AdminPrivateRoutes}
            />
          )}
          {isEezyguardAdmin && (
            <SidebarSection
              icon={<GppGood />}
              name="EezyLine"
              listItens={EezyGuardAdminRoutes}
            />
          )}
          <SidebarMyProfileSection unreadNotificationCount={unreadNotificationCount} />
        </Box>
      </Drawer>

      <Toolbar />
    </Box>
  );
};

export default Header;
